// src/components/TourContainer.js
import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import placeholderLogo from "MainApp/assets/visual/vectors/UI/logos/ImagePlaceholder.svg";
import "./TourContainer.css";
import {
  PButtonPure,
  PModal,
  PDivider,
} from "@porsche-design-system/components-react";
import LanguageSelector from "Components/LanguageSelector/LanguageSelector";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DefaultTitleMedia,
  HeadSelect,
  insertFadeInKeyframes,
  TitleMedia,
} from "MainApp/assets/visual/styles/globalStyles/globalJsxStyles";
import ClosingButton from "Components/ClosingButton/ClosingButton";
import { ClosingModal } from "Components/ClosingModal/ClosingModal";
import BorderedContainer from "Components/BorderedContainer/BorderedContainer";
import FooterPoweredBy from "Components/FooterPoweredBy/FooterPoweredBy";
import useIsPhone from "MainApp/assets/utils/hooks/useIsPhone";
import { IsRotatedContext } from "MainApp/assets/utils/context/isRotatedContext";
import {
  borderRadiusLarge,
  // frostedGlassStyle,
  spacingFluidMedium,
  spacingFluidXSmall,
  textSmallStyle,
  themeDarkPrimary,
  themeLightStateHover,
} from "@porsche-design-system/components-js/styles";
import { useTranslation } from "react-i18next";
import { AuthContext } from "MainApp/assets/utils/context/AuthContext";
import styled from "styled-components";
import { frostedGlassStyle } from "@porsche-design-system/components-react/styles";
// import { frostedGlassStyle } from '@porsche-design-system/components-{js|angular|react|vue}/styles';
import WebFont from "webfontloader";
import {
  isHeaderDurationEnabled,
  pickDuration,
} from "./headerTourDisplay";

const TourContainer = ({ children, screen }) => {
  const { eventId, userData, isTuk, isRetrotourIframe, optionalFields } =
    useContext(AuthContext);

  const { isRotated } = useContext(IsRotatedContext);
  const { t } = useTranslation("tourContainer");
  const navigate = useNavigate();
  const location = useLocation();
  const isTourInformationScreen = screen === "TourInformation";
  const routeState = location.state;

  const pickTitle = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value.trim();
    if (typeof value === "object") {
      const first = Object.values(value).find(
        (v) => typeof v === "string" && v.trim()
      );
      return first ? first.trim() : "";
    }
    return "";
  };

  const directHeaderTourTitle = useMemo(() => {
    if (!routeState) return "";
    return (
      pickTitle(routeState?.title) ||
      pickTitle(routeState?.tour?.title) ||
      pickTitle(routeState?.newTour?.title) ||
      ""
    );
  }, [routeState]);

  const directHeaderTourDuration = useMemo(() => {
    const queryDuration = new URLSearchParams(location.search).get("duration");

    return pickDuration(
      routeState?.duration,
      routeState?.selectedDuration,
      routeState?.tour?.selectedDuration,
      routeState?.tour?.duration,
      routeState?.newTour?.selectedDuration,
      routeState?.newTour?.duration,
      queryDuration
    );
  }, [location.search, routeState]);

  const [persistedHeaderTourTitle, setPersistedHeaderTourTitle] = useState(() => {
    try {
      return sessionStorage.getItem("skg_header_tour_title") || "";
    } catch {
      return "";
    }
  });
  const [persistedHeaderTourDuration, setPersistedHeaderTourDuration] =
    useState(() => {
      try {
        const value = sessionStorage.getItem("skg_header_tour_duration");
        return pickDuration(value);
      } catch {
        return null;
      }
    });

  useEffect(() => {
    if (location.pathname === "/") {
      setPersistedHeaderTourTitle("");
      setPersistedHeaderTourDuration(null);
      try {
        sessionStorage.removeItem("skg_header_tour_title");
        sessionStorage.removeItem("skg_header_tour_duration");
      } catch {}
      return;
    }

    if (directHeaderTourTitle) {
      setPersistedHeaderTourTitle(directHeaderTourTitle);
      try {
        sessionStorage.setItem("skg_header_tour_title", directHeaderTourTitle);
      } catch {}
    }
  }, [location.pathname, directHeaderTourTitle]);

  useEffect(() => {
    if (!directHeaderTourDuration) return;

    setPersistedHeaderTourDuration(directHeaderTourDuration);
    try {
      sessionStorage.setItem(
        "skg_header_tour_duration",
        String(directHeaderTourDuration)
      );
    } catch {}
  }, [directHeaderTourDuration]);

  const headerTourTitle = directHeaderTourTitle || persistedHeaderTourTitle;
  const headerTourDuration =
    directHeaderTourDuration || persistedHeaderTourDuration;
  const headerFallbackText = userData?.data?.companyName || t("defaultTitle");
  const shouldShowHeaderDuration = Boolean(
    headerTourTitle &&
      headerTourDuration &&
      isHeaderDurationEnabled(optionalFields)
  );

  const isPhone = useIsPhone();
  const [isModalReady, setIsModalReady] = useState(false);
  const [isList, setIsList] = useState(false);
  const [bgLogoStyle, setBgLogoStyle] = useState({ width: 0, height: 0 });
  const [isLogoReady, setIsLogoReady] = useState(false); // Controla si el Logo está listo
  const [shouldCheck, setShouldCheck] = useState(true); // Controla la comprobación constante
  const modalRef = useRef(null);
  const logoRef = useRef(null);
  const [titleTextStyling, setTitleTextStyling] = useState({});
  // Añade este efecto para detectar si estamos en un iframe del dominio específico
  // En el useEffect donde verificas el origen


  useEffect(() => {
    if (location.pathname === "/iframeList") {
      setIsList(true);
    } else {
      setIsList(false);
    }
  }, [location, setIsList]);
  const checkLogoDimensions = () => {
    if (logoRef.current) {
      const logoData = logoRef.current.getBoundingClientRect();
      if (logoData.width > 0 && logoData.height > 0) {
        setTitleTextStyling({ right: logoData.width * 3.5 });
        setBgLogoStyle({
          width: logoData.width * 3,
          height: logoData.width * 3,
        });
        setIsLogoReady(true); // Marcar el Logo como listo cuando tiene dimensiones válidas
        setShouldCheck(false); // Desactivar la comprobación constante
      }
    }
  };
  useEffect(() => {
    if (shouldCheck) {
      const interval = setInterval(checkLogoDimensions, 50); // Comprobación cada 50ms
      return () => clearInterval(interval);
    }
  }, [shouldCheck]);

  useEffect(() => {
    if (isLogoReady) {
      const updateLogoStyles = () => {
        if (logoRef.current) {
          const logoData = logoRef.current.getBoundingClientRect();
          setTitleTextStyling({ right: logoData.width * 6.5 });
          setBgLogoStyle({
            width: logoData.width * 6,
            height: logoData.width * 6,
          });
        }
      };

      // Usar ResizeObserver para observar cambios en las dimensiones del logo
      const resizeObserver = new ResizeObserver(updateLogoStyles);

      if (logoRef.current) {
        resizeObserver.observe(logoRef.current);
      }

      // Escuchar el evento de cambio de tamaño de la ventana
      const handleWindowResize = updateLogoStyles;
      window.addEventListener("resize", handleWindowResize);

      // Cleanup
      return () => {
        if (logoRef.current) {
          resizeObserver.unobserve(logoRef.current);
          resizeObserver.disconnect();
        }
        window.removeEventListener("resize", handleWindowResize);
      };
    }
  }, [isLogoReady]);

  useEffect(() => {
    insertFadeInKeyframes();
  }, []);

  useEffect(() => {
    if (!isPhone) {
      const handle = requestAnimationFrame(() => {
        setIsModalReady(true);
      });

      return () => cancelAnimationFrame(handle);
    }
  }, [isPhone]);

  const goBack = () => {
    window.history.back();
  };

  const [showCloseFlyout, setShowCloseFlyout] = useState(false);
  const [showBackFlyout, setShowBackFlyout] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(true);

  const goBackToFather = () => {
    setIsModalOpen(false);
    setShowCloseFlyout(false);

    // Enviar mensaje a la ventana padre para cerrar el iframe
    if (window.parent) {
      //   console.log("Enviando mensaje modalClosed al padre");
      setTimeout(window.parent.postMessage(`${eventId}`, "*"), 1100000); // Reemplaza con el dominio de la página padre
      setTimeout(window.parent.postMessage("modalClosed", "*"), 1200000); // Reemplaza con el dominio de la página padre
    }
  };

  const goBackToRoot = () => {
    navigate("/");
    setShowBackFlyout(false);
  };

  const handleCloseFlyoutShow = () => {
    setShowCloseFlyout(true);
  };
  const handleBackFlyoutShow = () => {
    setShowBackFlyout(true);
  };
  const handleCloseFlyoutClose = () => {
    setShowCloseFlyout(false);
  };
  const handleBackFlyoutClose = () => {
    setShowBackFlyout(false);
  };

  // #1
  const tourContainerGridStyling = {
    width: isPhone ? "100%" : "",
    flexGrow: isPhone ? "" : "1",
  };

  // #2
  // HeadSelect

  // #3
  // phoneHeader -> 4.75vh
  const phoneHeaderStyling = {
    height: isPhone ? (isRotated ? "4.75vw" : "4.75vh") : "4.75vh",
  };

  // #4
  const headMainWrapStyling = {
    position: isPhone ? "sticky" : "",
    height: isPhone ? (isRotated ? "10svw" : "14svh") : "14svh",
  };

  // #5
  const absoluteWrapperStyling = {
    height: isPhone ? (isRotated ? "8svw" : "12svh") : "12svh",
    maxHeight: isPhone ? (isRotated ? "8svw" : "12svh") : "12svh",
  };

  // #6
  //  mainHeadSelectStyling

  // #7
  // dividerContainer

  // #-
  const bodySelectStyling = {
    marginTop: isPhone ? (isRotated ? "14.75svw" : "18.75svh") : "14svh",
  };

  const handleLogoClick = () => {
    // Si la ruta actual comienza con "/reservation/", llama a la función
    if (location.pathname.startsWith("/reservation/")) {
      handleBackFlyoutShow();
      return;
    }

    // Si no estamos en "/", navega a "/"
    if (location.pathname !== "/") {
      navigate("/");
    }
  };

  // const frostedGlassStyling = isPhone && {
  //   // ...frostedGlassStyle,
  //   // background: themeLightStateHover,
  //   // borderRadius: borderRadiusLarge,
  //   position: "fixed",
  //   inset: "0",
  //   margin: "0",
  //   padding: "0",
  //   border: "0",
  //   width: "100dvw",
  //   height: "100dvh",
  //   maxWidth: "100dvw",
  //   maxHeight: "100dvh",
  //   overflow: "hidden",
  //   display: "block",
  //   outline: "0",
  //   zIndex: "9999999",
  //   visibility: "inherit",
  //   pointerEvents: "auto",
  //   background: "rgba(1, 2, 5, 0.67)",
  //   WebkitbackdropFilter: "blur(32px)",
  //   backdropFilter: "blur(32px)",
  //   transition:
  //     "visibility 0s linear var(--p-transition-duration, 0s), background-color var(--p-transition-duration, 0.6s) cubic-bezier(0,0,0.2,1), -webkit-backdrop-filter var(--p-transition-duration, 0.6s) cubic-bezier(0,0,0.2,1), backdrop-filter var(--p-transition-duration, 0.6s) cubic-bezier(0,0,0.2,1)",
  //   overlay: "auto !important",
  // };

  // const FrostedGlass = styled.div({
  //   ...frostedGlassStyle,
  //   color: themeDarkPrimary,
  //   background: themeLightStateHover,
  //   borderRadius: borderRadiusLarge,
  //   width: "100%",
  //   height: "100%",
  //   // padding: spacingFluidMedium,
  // });
  const shouldRenderDiv = !location.pathname.includes("/reservation");
  useEffect(() => {
    WebFont.load({
      google: {
        families: ["Bebas Neue:400,700&display=swap"],
      },
    });
  }, []);
  const content = (
    <div
      className="tourContainerGrid stretch-to-full-modal-width"
      style={{
        ...tourContainerGridStyling,
        //  ...frostedGlassStyling
      }}
    >
      {!isList && (
        <HeadSelect isPhone={isPhone}>
          {isPhone && (
            <div className="phoneHeader" style={phoneHeaderStyling}>
              <LanguageSelector
                className="languageSelectorPhone"
                isPhone={isPhone}
              />

              <ClosingButton onClick={handleCloseFlyoutShow} />
            </div>
          )}
          {!isPhone && <LanguageSelector />}
          <div className="headMainWrap" style={headMainWrapStyling}>
            <div className="absoluteWrapper" style={absoluteWrapperStyling}>
              <div
                className="mainHeadSelectWrapper"
                style={isTuk ? { backgroundColor: "#0056b3" } : {}}
              >
                <div
                  className={`backForwardButtonContainerOnHeadSelect ${
                    isTourInformationScreen
                      ? "backForwardButtonContainerOnHeadSelect--visible"
                      : "backForwardButtonContainerOnHeadSelect--hidden"
                  }`}
                >
                  <PButtonPure
                    style={{ padding: "1rem", height: "100%" }}
                    tabIndex={isTourInformationScreen ? 0 : -1}
                    hideLabel={true}
                    icon="arrow-left"
                    className="pButtonBackForwardHeadSelect"
                    onClick={isTourInformationScreen ? goBack : undefined}
                  />
                </div>
                <div className="textTitleContainer" style={titleTextStyling}>
                  {headerTourTitle ? (
                    <div
                      className={`textTitleStack ${
                        shouldShowHeaderDuration
                          ? "textTitleStack--with-duration"
                          : ""
                      }`}
                    >
                      <TitleMedia
                        className="textTitleContent"
                        style={{
                          ...(isTuk && {
                            color: "white",
                          }),
                        }}
                      >
                        {headerTourTitle}
                      </TitleMedia>
                      {shouldShowHeaderDuration && (
                        <span
                          className="textTitleDuration"
                          style={{
                            ...(isTuk && {
                              color: "white",
                            }),
                          }}
                        >
                          {headerTourDuration} min.
                        </span>
                      )}
                    </div>
                  ) : (
                    <DefaultTitleMedia
                      className="textTitleContent"
                      style={{
                        marginLeft: spacingFluidXSmall,
                        ...(isRetrotourIframe && {
                          fontFamily:
                            "'Sorts Mill Goudy', 'Your-Special-Font', sans-serif",
                          fontSize: "1.25rem",
                          fontWeight: 700,
                          letterSpacing: "0.05em",
                          lineHeight: "2.5rem",
                          paddingLeft: spacingFluidXSmall,
                        }),
                        ...(isTuk && {
                          color: "white",
                        }),
                      }}
                    >
                      {headerFallbackText}
                    </DefaultTitleMedia>
                  )}
                </div>
                <div className="logosContainer">
                  {userData?.data?.companyLogo ? (
                    <>
                      <img
                        alt=""
                        ref={logoRef}
                        src={userData?.data?.companyLogo}
                        className="logo"
                        onClick={handleLogoClick}
                        onKeyDown=""
                        onLoad={checkLogoDimensions} // Asegura medir cuando la imagen se cargue
                      />
                      {isLogoReady && (
                        <img
                          alt=""
                          src={userData?.data?.companyLogo}
                          className="bgLogo"
                          style={bgLogoStyle}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      <img
                        alt=""
                        ref={logoRef}
                        src={placeholderLogo}
                        className="logo"
                        onClick={handleLogoClick}
                        onKeyDown=""
                        onLoad={checkLogoDimensions}
                      />
                      {isLogoReady && (
                        <img
                          alt=""
                          src={placeholderLogo}
                          className="bgLogo"
                          style={bgLogoStyle}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
              <PDivider className="dividerOnContainer w-full" />
            </div>
          </div>
        </HeadSelect>
      )}
      <div className="bodySelect" style={bodySelectStyling}>
        {children}
      </div>
      {!isList && shouldRenderDiv && (
        <FooterPoweredBy isPhone={isPhone} PProvider={true} />
      )}
    </div>
  );

  return (
    <>
      {/* <FrostedGlass> */}

      {isPhone || isList
        ? content
        : isModalReady && (
            <PModal
              open={isModalOpen}
              ref={modalRef}
              className="tourContainerModal"
              onDismiss={handleCloseFlyoutShow}
              disableBackdropClick={true}
              style={{
                "--p-modal-width": "80svw",
              }}
              aria={{ "aria-label": t("arias.systemNameAria") }}
            >
              <BorderedContainer>{content}</BorderedContainer>
            </PModal>
          )}
      {showCloseFlyout && (
        <ClosingModal
          style={{
            "--p-modal-width": "",
          }}
          accept={goBackToFather}
          onDismiss={handleCloseFlyoutClose}
        />
      )}
      {showBackFlyout && (
        <ClosingModal
          style={{
            "--p-modal-width": "",
          }}
          accept={goBackToRoot}
          onDismiss={handleBackFlyoutClose}
        />
      )}
      {/* </FrostedGlass> */}
    </>
  );
};

export default TourContainer;
